import 'swiper/swiper-bundle.css';

import KmxArrowRightStroked from '@kmx/mui-icons/KmxArrowRightStroked';
import KmxChevronLeftStroked from '@kmx/mui-icons/KmxChevronLeftStroked';
import KmxChevronRightStroked from '@kmx/mui-icons/KmxChevronRightStroked';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { memo, useCallback, useRef, useState } from 'react';
import { A11y, Navigation, Pagination } from 'swiper/modules';
import { SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperClass } from 'swiper/types';

import { EXIF_TAGS } from '@/constants/exif-tags';
import { TaggingEvent } from '@/context/tagging';
import useTagging from '@/hooks/useTagging';
import type { GalleryConfig } from '@/types/GalleryConfig';

import {
  HeroImageSlide,
  ImageSlide,
  LoaderContainer,
  NavButton,
  StyledSwiper,
  ViewMoreSlide,
} from './ImageGallery.styles';

const swiperModules = [A11y, Navigation, Pagination];
const a11yConfig = { enabled: true };
const paginationConfig = { enabled: true };

type AdditionalImage = {
  src: string;
  alt: string;
};

export type ImageGalleryProps = {
  alt: string;
  href: string;
  stockNumber: string;
  heroImageSrc: string;
  onChange?: GalleryConfig['onChange'];
  swiperProps?: GalleryConfig['swiperProps'];
  onImagesLoaded?: GalleryConfig['onImagesLoaded'];
  loadAdditionalImages?: GalleryConfig['loadAdditionalImages'];
};

function ImageGallery({
  alt,
  href,
  onChange,
  swiperProps,
  stockNumber,
  heroImageSrc,
  onImagesLoaded,
  loadAdditionalImages = true,
}: ImageGalleryProps) {
  const { dispatchTaggingEvent } = useTagging();

  const nextButton = useRef<HTMLButtonElement | null>(null);
  const prevButton = useRef<HTMLButtonElement | null>(null);

  const [state, setState] = useState<{
    images: AdditionalImage[];
    imagesLoaded: boolean;
    loading: boolean;
  }>({ images: [], imagesLoaded: false, loading: false });

  /**
   * Initialize Swiper.
   */
  const handleInit = useCallback((swiper: SwiperClass) => {
    // Configure navigation.
    if (typeof swiper.params.navigation === 'object') {
      swiper.params.navigation.enabled = true;
      swiper.params.navigation.nextEl = nextButton.current;
      swiper.params.navigation.prevEl = prevButton.current;

      swiper.navigation.init();
      swiper.navigation.update();
    }
  }, []);

  /**
   * Load additional images from the API.
   */
  const loadImages = useCallback(async () => {
    try {
      const response = await fetch(
        `https://img2.carmax.com/api/subject/${stockNumber}`,
      );

      if (!response.ok) {
        throw new Error('Response not OK.');
      }

      const data: {
        items: Array<
          Record<string, unknown> & {
            thumbnailUrl: string;
            metadata?: {
              exifTag?: string;
            };
          }
        >;
      } = await response.json();

      onImagesLoaded?.(true);

      if (!data || !data?.items) {
        return [];
      }

      return EXIF_TAGS.map((tag) => {
        const image = data.items.find((item) =>
          item?.metadata?.exifTag?.endsWith(tag.tag),
        );

        return image
          ? { src: image.thumbnailUrl, alt: tag.description }
          : undefined;
      }).filter((item) => item !== undefined);
    } catch {
      console.error('Unable to load additional car images.');
      onImagesLoaded?.(false);

      return [];
    }
  }, [onImagesLoaded, stockNumber]);

  /**
   * Responds to slide changes. On the first one, it will attempt to load
   * additional images.
   */
  const handleSlideChange = useCallback(
    async (swiper: SwiperClass) => {
      onChange?.(swiper.activeIndex, swiper.previousIndex);

      if (state.imagesLoaded || !loadAdditionalImages) {
        return;
      }

      setState((prev) => ({ ...prev, loading: true }));

      swiper.disable();

      // Add some initial latency to allow the loading spinner to be seen.
      await new Promise((resolve) => {
        setTimeout(resolve, 250);
      });

      const images = await loadImages();

      setState((prev) => ({
        ...prev,
        imagesLoaded: true,
        images,
        loading: false,
      }));

      requestAnimationFrame(() => {
        swiper.enable();
        swiper.update();
        swiper.pagination.update();
        swiper.navigation.update();
      });
    },
    [loadAdditionalImages, loadImages, onChange, state.imagesLoaded],
  );

  /**
   * Clicking on an image slide navigates to the car details page
   * This handles any additional click functionality.
   */
  const handleImageClickNavigation = useCallback(() => {
    dispatchTaggingEvent({
      event: TaggingEvent.ClickCarTile,
      data: {
        text: alt,
      },
    });
  }, [alt, dispatchTaggingEvent]);

  const galleryClickProps = `Element type: Car Tile Image,StockNumber: ${stockNumber}`;
  const galleryNavClickProps = 'Element type: Image Carousel Nav';

  return (
    <StyledSwiper
      a11y={a11yConfig}
      data-loading={state.loading}
      followFinger={state.imagesLoaded || !loadAdditionalImages}
      modules={swiperModules}
      onSlideChange={handleSlideChange}
      onSwiper={handleInit}
      pagination={paginationConfig}
      speed={state.imagesLoaded || !loadAdditionalImages ? 300 : 0}
      className="kmx-car-tile__image-gallery"
      data-clickprops={galleryClickProps}
      slidesPerView={1}
      {...swiperProps}
    >
      <SwiperSlide>
        <ImageSlide
          href={href}
          rel="nofollow"
          tabIndex={-1}
          data-ignore-anchor-tag-rule
          onClick={handleImageClickNavigation}
        >
          <img loading="lazy" alt={alt} src={heroImageSrc} />
        </ImageSlide>
      </SwiperSlide>
      {loadAdditionalImages && !state.imagesLoaded ? (
        <SwiperSlide>
          <HeroImageSlide
            data-ignore-anchor-tag-rule
            href={href}
            rel="nofollow"
            tabIndex={-1}
          >
            <img loading="lazy" alt={alt} src={heroImageSrc} />
            <LoaderContainer>
              {state.loading ? <CircularProgress /> : null}
            </LoaderContainer>
          </HeroImageSlide>
        </SwiperSlide>
      ) : null}
      {state.images.map((image) => (
        <SwiperSlide key={image.src}>
          <ImageSlide
            href={href}
            rel="nofollow"
            tabIndex={-1}
            data-ignore-anchor-tag-rule
            onClick={handleImageClickNavigation}
          >
            <img alt={`${alt} - ${image.alt}`} src={image.src} />
          </ImageSlide>
        </SwiperSlide>
      ))}
      <SwiperSlide>
        <ViewMoreSlide
          data-ignore-anchor-tag-rule
          href={href}
          rel="nofollow"
          tabIndex={-1}
          onClick={handleImageClickNavigation}
        >
          <Button
            color="secondary"
            component="span"
            data-ignore-anchor-tag-rule
            endIcon={<KmxArrowRightStroked />}
            role={undefined}
            size="large"
            tabIndex={-1}
            variant="text"
          >
            View more
          </Button>
        </ViewMoreSlide>
      </SwiperSlide>
      <NavButton
        aria-label="Previous"
        color="default"
        data-clickprops={galleryNavClickProps}
        data-direction="prev"
        data-ignore-anchor-tag-rule
        ref={prevButton}
        size="small"
        variant="outlined"
      >
        <KmxChevronLeftStroked />
      </NavButton>
      <NavButton
        aria-label="Next"
        color="default"
        data-clickprops={galleryNavClickProps}
        data-direction="next"
        data-ignore-anchor-tag-rule
        ref={nextButton}
        size="small"
        variant="outlined"
      >
        <KmxChevronRightStroked />
      </NavButton>
    </StyledSwiper>
  );
}

export default memo(ImageGallery);
